import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { createChatTag, updateChatTag } from '../../../hooks/chat/api';
import {
  ChatTag,
  ChatTagCategory,
  FAMILY_LABELS,
  HARDWARE_FAMILIES,
  HardwareFamily,
  RIDER_REPORT_TAG_IDS,
  familyToGraphql,
  similarActiveTag,
  tagIdPreview,
} from '../tagGlossary';

/** ant's column limits. */
export const LABEL_MAX = 120;
export const TEXT_MAX = 1000;
export const RIDER_REPORT_ID_MAX = 60;

/** A whole number from 1, as ant's `position` (integer, min 1) takes it. */
export const isWholePositionFrom1 = (text: string): boolean =>
  /^\d+$/.test(text.trim()) && Number(text) >= 1;

interface Props {
  open: boolean;
  category: ChatTagCategory;
  /** The tag being edited; null for a new one. */
  tag: ChatTag | null;
  onClose: () => void;
  /** Saved in ant: the page says so and re-reads the glossary. */
  onSaved: (what: 'added' | 'saved') => void;
  /** The page says why when ant refuses. */
  onError: (error: unknown) => void;
}

interface Draft {
  label: string;
  definition: string;
  notes: string;
  appliesTo: HardwareFamily[];
  position: string;
  riderReportTagId: string;
}

const emptyDraft = (): Draft => ({
  label: '',
  definition: '',
  notes: '',
  appliesTo: [],
  position: '',
  riderReportTagId: '',
});

const fromTag = (tag: ChatTag): Draft => ({
  label: tag.label,
  definition: tag.definition ?? '',
  notes: tag.notes ?? '',
  appliesTo: tag.appliesTo,
  position: String(tag.position),
  riderReportTagId: tag.riderReportTagId ?? '',
});

/** Why the draft cannot be saved yet, or null. */
export function tagDraftProblem(draft: Draft): string | null {
  if (!draft.label.trim()) return 'Give the tag a label.';
  if (draft.label.trim().length > LABEL_MAX)
    return `The label is at most ${LABEL_MAX} characters.`;
  if (draft.definition.length > TEXT_MAX)
    return `The definition is at most ${TEXT_MAX} characters.`;
  if (draft.notes.length > TEXT_MAX)
    return `The notes are at most ${TEXT_MAX} characters.`;
  if (draft.position.trim() && !isWholePositionFrom1(draft.position))
    return 'The position is a whole number from 1.';
  return null;
}

const orNull = (text: string): string | null => (text.trim() ? text.trim() : null);

/**
 * Add or edit one tag of a category. The Add dialog shows the id ant will
 * create and warns when an active tag of the category already has the
 * label (folded). A tag never changes category: the edit dialog has no
 * category field. The rider report id is free text with the app's ids
 * offered; ant checks the format.
 */
export function TagDialog({ open, category, tag, onClose, onSaved, onError }: Props) {
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(tag ? fromTag(tag) : emptyDraft());
  }, [open, tag]);

  const preview = useMemo(
    () =>
      !tag && draft.label.trim() ? tagIdPreview(category, draft.label.trim()) : null,
    [tag, category, draft.label]
  );
  const similar = useMemo(
    () => similarActiveTag(category, draft.label, tag?.id),
    [category, draft.label, tag]
  );
  const problem = tagDraftProblem(draft);

  const save = async () => {
    if (problem) return;
    setSaving(true);
    try {
      const fields = {
        label: draft.label.trim(),
        definition: orNull(draft.definition),
        notes: orNull(draft.notes),
        appliesTo: draft.appliesTo.map(familyToGraphql),
        riderReportTagId: orNull(draft.riderReportTagId),
      };
      if (tag) {
        await updateChatTag(tag.id, {
          ...fields,
          position: Number(draft.position) || tag.position,
        });
      } else {
        await createChatTag({
          categoryId: category.id,
          ...fields,
          ...(draft.position.trim() ? { position: Number(draft.position) } : {}),
        });
      }
      onSaved(tag ? 'saved' : 'added');
      onClose();
    } catch (error) {
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{tag ? 'Edit tag' : `New tag in ${category.name}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            autoFocus
            label="Label"
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            inputProps={{ maxLength: LABEL_MAX, 'aria-label': 'Label' }}
            helperText={preview ? `Id: ${preview}` : `${draft.label.length}/${LABEL_MAX}`}
            size="small"
          />
          {similar && (
            <Alert severity="warning" data-testid="similar-tag">
              Similar tag exists: {similar.label}
            </Alert>
          )}
          <TextField
            label="Definition"
            value={draft.definition}
            onChange={(e) => setDraft({ ...draft, definition: e.target.value })}
            inputProps={{ maxLength: TEXT_MAX, 'aria-label': 'Definition' }}
            multiline
            minRows={2}
            maxRows={8}
            size="small"
          />
          <TextField
            label="Notes"
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            inputProps={{ maxLength: TEXT_MAX, 'aria-label': 'Notes' }}
            multiline
            minRows={1}
            maxRows={6}
            size="small"
          />
          <Autocomplete<HardwareFamily, true, false, false>
            multiple
            size="small"
            options={HARDWARE_FAMILIES}
            value={draft.appliesTo}
            onChange={(_, value) => setDraft({ ...draft, appliesTo: value })}
            getOptionLabel={(f) => FAMILY_LABELS[f]}
            renderTags={(value, getTagProps) =>
              value.map((f, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip {...tagProps} key={key} label={FAMILY_LABELS[f]} size="small" />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Applies to"
                helperText="Empty means every rental"
                inputProps={{ ...params.inputProps, 'aria-label': 'Applies to' }}
              />
            )}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Position"
              value={draft.position}
              onChange={(e) => setDraft({ ...draft, position: e.target.value })}
              inputProps={{ inputMode: 'numeric', 'aria-label': 'Position' }}
              helperText={tag ? 'Order in the category' : 'Empty: last in the category'}
              size="small"
              sx={{ width: 160 }}
            />
            <TextField
              label="Rider report id"
              value={draft.riderReportTagId}
              onChange={(e) => setDraft({ ...draft, riderReportTagId: e.target.value })}
              inputProps={{
                list: 'rider-report-tag-ids',
                maxLength: RIDER_REPORT_ID_MAX,
                'aria-label': 'Rider report id',
              }}
              helperText="The app's report this tag stands for (phase 2)"
              size="small"
              sx={{ flex: 1 }}
            />
            <datalist id="rider-report-tag-ids" data-testid="rider-report-tag-ids">
              {RIDER_REPORT_TAG_IDS.map((id) => (
                <option value={id} key={id} data-testid="rider-report-tag-id" />
              ))}
            </datalist>
          </Stack>
          {tag && (
            <Typography variant="caption" color="text.secondary">
              A tag stays in its category; retire it and add a new one to move it.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || Boolean(problem)}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {tag ? 'Save' : 'Add tag'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
